import CustomCSS from '@package/components/manage/CustomCSS/CustomCSS';
import CustomJS from '@package/components/manage/CustomJS/CustomJS';
import GridCardListing from '@package/components/manage/Blocks/Listing/GridCardListing';
import MediaPageSummaryTemplate from '@package/components/manage/Blocks/Listing/MediaPageSummaryTemplate';
import MediaPageSummaryGridTemplate from '@package/components/manage/Blocks/Listing/MediaPageSummaryGridTemplate';
import RightColumnFacets from '@plone/volto/components/manage/Blocks/Search/layout/RightColumnFacets';
import LeftColumnFacets from '@plone/volto/components/manage/Blocks/Search/layout/LeftColumnFacets';
import { SelectFacetFilterListEntry } from '@plone/volto/components/manage/Blocks/Search/components';

import { defineMessages } from 'react-intl';

import '@plone/volto/config';

import { BodyInjection } from '@package/components';
import TreeFacet from '@package/components/manage/Blocks/Search/TreeFacet';

// For customizations
defineMessages({
  contactUs: {
    id: "We apologize for the inconvenience, but you don't have permissions on this resource.",
    defaultMessage:
      "We apologize for the inconvenience, but you don't have permissions on this resource.",
  },
});

export default function applyConfig(config) {
  // VideoJS
  config.settings.videoJSAllowedOptions = [
    'controls',
    'fluid',
    'height',
    'playbackRates',
    'responsive',
    'sources',
    'poster',
    'audioOnlyMode',
    'autoplay',
    'width',
    'loop',
    'liveui',
    'muted',
    'watermark',
    'subtitles',
    'aspectRatio',
  ];

  config.settings.defaultPageSize = 20;

  // Timezone used for date formatting in SSR and client to prevent hydration
  // mismatches. Without this, the server (typically UTC) and client (user's
  // browser timezone) may format the same date differently.
  config.settings.dateFormatTimeZone = 'Europe/Helsinki';

  config.settings.isMultilingual = config.settings.isMultilingual || false;
  config.settings.supportedLanguages = config.settings.supportedLanguages || [
    'en',
  ];
  config.settings.defaultLanguage = config.settings.defaultLanguage || 'en';
  config.settings.publicPath = '';
  config.settings.defaultUseInitialPrompt =
    config.settings.defaultUseInitialPrompt ?? false;

  // volto-light-theme setting
  config.settings.enableFatMenu = false;

  config.blocks.blocksConfig.gridBlock.allowedBlocks.push('embedmedia');
  config.blocks.blocksConfig.gridBlock.blocksConfig = {
    ...config.blocks.blocksConfig.gridBlock.blocksConfig,
    embedmedia: config.blocks.blocksConfig.embedmedia,
  };

  config.blocks.blocksConfig.listing.variations = [
    {
      id: 'mediapagesummary',
      title: 'MediaPageSummary',
      template: MediaPageSummaryTemplate,
      fullobjects: false,
    },
    {
      id: 'mediapagesummarygrid',
      title: 'MediaPageSummaryGrid',
      template: MediaPageSummaryGridTemplate,
      fullobjects: false,
    },
    {
      id: 'gridcardlisting',
      title: 'Grid Card Listing',
      template: GridCardListing,
      fullobjects: false,
    },
    ...config.blocks.blocksConfig.listing.variations,
  ];

  // Extend listing block with a hide property
  config.blocks.blocksConfig.listing.schemaEnhancer = ({
    schema,
    formData,
    intl,
  }) => {
    schema.properties.hideIfEmpty = {
      title: 'Hide if empty',
      type: 'boolean',
      default: false,
      description:
        'If checked, the block will not render when there are no items to display.',
    };
    schema.fieldsets[0].fields.push('hideIfEmpty');

    return schema;
  };

  config.blocks.blocksConfig.search = {
    ...config.blocks.blocksConfig.search,
    variations: [
      ...config.blocks.blocksConfig.search.variations,
      {
        id: 'facetsRightSide',
        title: 'Facets on right',
        view: RightColumnFacets,
        isDefault: true,
      },
      {
        id: 'facetsLeftSide',
        title: 'Facets on left',
        view: LeftColumnFacets,
        isDefault: false,
      },
    ],
    extensions: {
      ...config.blocks.blocksConfig.search.extensions,
      facetWidgets: {
        ...config.blocks.blocksConfig.search.extensions.facetWidgets,
        types: [
          ...config.blocks.blocksConfig.search.extensions.facetWidgets.types,
          {
            id: 'treeFacet',
            title: 'Tree',
            view: TreeFacet,
            isDefault: false,
            stateToValue: TreeFacet.stateToValue,
            valueToQuery: TreeFacet.valueToQuery,
            schemaEnhancer: TreeFacet.schemaEnhancer,
            filterListComponent: SelectFacetFilterListEntry,
          },
        ],
      },
    },
  };

  // Hide some blocks from UI
  const hiddenBlocks = [
    'mediapagevideojs',
    'downloads',
    'uploadbutton',
    'video',
  ];
  Object.keys(config.blocks.blocksConfig).forEach((blockId, idx) => {
    const blockConfig = config.blocks.blocksConfig[blockId];
    const originalRestricted = blockConfig.restricted;
    blockConfig.restricted = (props) => {
      if (hiddenBlocks.includes(blockId)) {
        return true;
      }
      return originalRestricted;
    };
  });

  config.settings.workflowMapping = {
    ...config.settings.workflowMapping,
    unlisted: { title: 'Unlisted', color: '#800080' },
    unlist: { title: 'Unlist', color: '#800080' },
  };

  config.settings.appExtras = [
    ...config.settings.appExtras,
    {
      match: '',
      component: BodyInjection,
    },
    {
      match: '',
      component: CustomCSS,
    },
    {
      match: '',
      component: CustomJS,
    },
  ];

  if (__SERVER__) {
    const myMiddleware = require('@package/middleware');
    config.settings.expressMiddleware.push(myMiddleware.default(config));
  }

  return config;
}
